import { useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { GET_BANNER } from "@atlas-int/data";
import type { GetBannerQuery } from "@atlas-int/data/graphql";
import { gridAreas } from "@atlas-int/tokens/layout";
import styled from "@emotion/styled";
import {
  spacing,
  fontSize,
  brand,
  text as textColor,
} from "@atlas-int/tokens";
import { CloseButton, ChevronRight } from "@atlas-int/ui";

export const InfoBannerContainer = styled.div`
  grid-area: ${gridAreas.infoBanner};
  width: 100%;
  background-color: ${brand.light};
  padding-top: ${spacing[4]};
  padding-bottom: ${spacing[4]};
  text-align: center;
  color: ${textColor.onDark};
  font-size: ${fontSize.lg};
`;

type BannerRow = NonNullable<
  NonNullable<GetBannerQuery["banner"]>[number]
>;

const announcementBannerKey = "lastSeenBannerId";
const isStorageAvailable = (type: string) => {
  let storage: typeof localStorage;
  try {
    storage = (window as any)[type];
    const x = "__storage_test__";
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1_014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === "QuotaExceededError" ||
        // Firefox
        e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage!.length !== 0
    );
  }
};

const InfoBanner = () => {
  const { data } = useQuery<GetBannerQuery>(GET_BANNER);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [showBanner, setShowBanner] = useState(false);
  const [activeBanners, setActiveBanners] = useState<BannerRow[]>([]);

  useEffect(() => {
    if (data?.banner) {
      //   requestIdleCallback(() => {
      const currentTime = new Date().getTime();
      const filteredBanners = data.banner.filter((banner): banner is BannerRow => {
        if (!banner?.startTime || !banner.endTime) return false;
        const startTime = new Date(banner.startTime).getTime();
        const endTime = new Date(banner.endTime).getTime();
        return currentTime >= startTime && currentTime <= endTime;
      });

      setActiveBanners(filteredBanners);

      if (filteredBanners.length > 0 && isStorageAvailable("localStorage")) {
        const localStorage = window.localStorage;
        const dismissedBanners = JSON.parse(
          localStorage.getItem(announcementBannerKey) || "[]",
        );

        // Ensure dismissedBanners is an array
        const dismissedBannerIds = Array.isArray(dismissedBanners)
          ? dismissedBanners
          : [];

        const unseenBanners = filteredBanners.filter(
          (banner) =>
            !dismissedBannerIds.map(String).includes(String(banner.bannerId)),
        );

        if (unseenBanners.length > 0) {
          setShowBanner(true);
          setCurrentBannerIndex(0);
          setActiveBanners(unseenBanners);
        } else {
          setShowBanner(false);
        }
      }
      //   });
    }
  }, [data]);

  const markBannerAsSeen = (bannerId: string | number) => {
    if (isStorageAvailable("localStorage")) {
      const localStorage = window.localStorage;
      const dismissedBanners = JSON.parse(
        localStorage.getItem(announcementBannerKey) || "[]",
      );

      const dismissedBannerIds = Array.isArray(dismissedBanners)
        ? dismissedBanners
        : [];

      if (!dismissedBannerIds.map(String).includes(String(bannerId))) {
        dismissedBannerIds.push(bannerId);
        localStorage.setItem(
          announcementBannerKey,
          JSON.stringify(dismissedBannerIds),
        );
      }
    }
  };

  const dismissBanner = () => {
    const currentBannerId = activeBanners[currentBannerIndex].bannerId;
    markBannerAsSeen(currentBannerId);

    if (currentBannerIndex < activeBanners.length - 1) {
      setCurrentBannerIndex(currentBannerIndex + 1);
    } else {
      setShowBanner(false);
    }
  };

  const showNextBanner = () => {
    const currentBannerId = activeBanners[currentBannerIndex].bannerId;
    markBannerAsSeen(currentBannerId);

    if (currentBannerIndex < activeBanners.length - 1) {
      setCurrentBannerIndex(currentBannerIndex + 1);
    } else {
      setShowBanner(false);
    }
  };

  if (!showBanner || activeBanners.length === 0) return null;

  const { text, ctaText, ctaLink } = activeBanners[currentBannerIndex];

  return (
    <InfoBannerContainer className="visible">
      <div
        id="info-banner"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div />
        <span>
          <span style={{ flexGrow: 1 }}>{text}</span>{" "}
          <a
            style={{ color: textColor.onDark, textDecoration: "underline" }}
            href={ctaLink}
          >
            {ctaText}
          </a>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
          <CloseButton
            size="sm"
            onClick={dismissBanner}
            label="dismiss"
            style={{ color: textColor.onDark }}
          />
          {currentBannerIndex < activeBanners.length - 1 && (
            <button
              onClick={showNextBanner}
              aria-label="next banner"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: textColor.onDark,
                display: "inline-flex",
                alignItems: "center",
                padding: 0,
              }}
            >
              <ChevronRight size={20} />
            </button>
          )}
        </span>
      </div>
    </InfoBannerContainer>
  );
};

export default InfoBanner;
